import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, decimal, boolean, timestamp, jsonb } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const properties = pgTable("properties", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  address: text("address").notNull(),
  city: text("city").notNull(),
  state: text("state").notNull(),
  zipCode: text("zip_code"),
  price: decimal("price", { precision: 12, scale: 2 }).notNull(),
  bedrooms: integer("bedrooms"),
  bathrooms: decimal("bathrooms", { precision: 3, scale: 1 }),
  squareFeet: integer("square_feet"),
  propertyType: text("property_type").notNull(),
  description: text("description"),
  images: jsonb("images").$type<string[]>().default([]), // Stores all scraped images
  allImageUrls: jsonb("all_image_urls").$type<string[]>().default([]), // Complete image gallery
  features: jsonb("features").$type<string[]>().default([]),
  latitude: decimal("latitude", { precision: 10, scale: 8 }),
  longitude: decimal("longitude", { precision: 11, scale: 8 }),
  isFeatured: boolean("is_featured").default(false),
  isActive: boolean("is_active").default(true),
  isWaterfront: boolean("is_waterfront").default(true), // All our properties are waterfront
  agentId: varchar("agent_id"),
  lastScraped: timestamp("last_scraped").default(sql`now()`), // Track when last updated
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

export const agents = pgTable("agents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  email: text("email").notNull().unique(),
  password: text("password"), // Hashed password for agent login
  phone: text("phone").notNull(),
  photo: text("photo"),
  bio: text("bio"),
  specialties: jsonb("specialties").$type<string[]>().default([]),
  rating: decimal("rating", { precision: 2, scale: 1 }).default("5.0"),
  reviewCount: integer("review_count").default(0),
  isActive: boolean("is_active").default(true),
});

export const leads = pgTable("leads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  email: text("email").notNull(),
  phone: text("phone").notNull(),
  propertyId: varchar("property_id").notNull(),
  agentId: varchar("agent_id"),
  leadType: text("lead_type").notNull(), // 'tour', 'contact', 'info'
  preferredTime: text("preferred_time"),
  message: text("message"),
  status: text("status").notNull().default("new"), // 'new', 'contacted', 'scheduled', 'completed', 'closed'
  potentialCommission: decimal("potential_commission", { precision: 10, scale: 2 }),
  createdAt: timestamp("created_at").default(sql`now()`),
});

export const insertPropertySchema = createInsertSchema(properties).omit({
  id: true,
  createdAt: true,
});

export const insertAgentSchema = createInsertSchema(agents).omit({
  id: true,
});

export const insertLeadSchema = createInsertSchema(leads).omit({
  id: true,
  createdAt: true,
});

export type Property = typeof properties.$inferSelect;
export type InsertProperty = z.infer<typeof insertPropertySchema>;
export type Agent = typeof agents.$inferSelect;
export type InsertAgent = z.infer<typeof insertAgentSchema>;
export type Lead = typeof leads.$inferSelect;
export type InsertLead = z.infer<typeof insertLeadSchema>;
